import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { CreditCard, Building2, CheckCircle, Copy, ArrowRight, Shield, Clock, Euro, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Coordonnées bancaires pour le virement - OBJECTIF SCOP OUTREMER
const BANK_DETAILS = {
  beneficiaire: "OBJECTIF SCOP OUTREMER",
  iban: "FR76 1695 8000 0182 4755 2649 230",
  bic: "QNTOFRP1XXX",
  banque: "Qonto",
  reference: "À préciser lors du virement"
};

export default function PaymentChoiceModal({ 
  isOpen, 
  onClose, 
  amount, 
  packageId,
  packageName,
  description,
  onPaymentInitiated,
  metadata = {}
}) {
  const { getAuthHeader, user } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [virementReference, setVirementReference] = useState('');

  // Générer une référence unique pour le virement
  const generateVirementReference = () => {
    const date = new Date();
    const ref = `VIR-${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setVirementReference(ref);
    return ref;
  };

  // Copier dans le presse-papier
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  // Initier le paiement par carte (Stripe)
  const initiateCardPayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/payments/checkout`,
        {
          package_id: packageId,
          origin_url: window.location.origin,
          metadata: {
            source: 'payment_choice_modal',
            user_email: user?.email || 'anonymous',
            ...metadata
          }
        },
        { headers: getAuthHeader() }
      );

      if (response.data.checkout_url) {
        if (onPaymentInitiated) onPaymentInitiated('card', response.data);
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initiation du paiement');
      setLoading(false);
    }
  };

  // Enregistrer le choix de virement bancaire
  const initiateBankTransfer = async () => {
    setLoading(true);
    const ref = generateVirementReference();
    
    try {
      // Enregistrer la demande de virement en base
      await axios.post(
        `${API}/api/payments/bank-transfer`,
        {
          package_id: packageId,
          amount: amount,
          reference: ref,
          metadata: {
            source: 'payment_choice_modal',
            user_email: user?.email || 'anonymous',
            package_name: packageName,
            ...metadata
          }
        },
        { headers: getAuthHeader() }
      );
      
      if (onPaymentInitiated) onPaymentInitiated('bank_transfer', { reference: ref });
      setShowBankDetails(true);
    } catch {
      // Même si l'API échoue, on affiche les coordonnées bancaires
      setShowBankDetails(true);
    } finally {
      setLoading(false);
    }
  };

  // Confirmer le choix de paiement
  const handleConfirm = () => {
    if (selectedMethod === 'card') {
      initiateCardPayment();
    } else if (selectedMethod === 'bank_transfer') {
      initiateBankTransfer();
    }
  };

  // Réinitialiser l'état à la fermeture
  const handleClose = () => {
    setSelectedMethod(null);
    setShowBankDetails(false);
    setVirementReference('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Euro className="h-5 w-5 text-[#d4af37]" />
            Choisir votre mode de paiement
          </DialogTitle>
          <DialogDescription>
            {packageName && (
              <span className="block mt-1">
                <strong>{packageName}</strong> - {amount?.toFixed(2)} €
              </span>
            )}
            {description && <span className="block text-sm mt-1">{description}</span>}
          </DialogDescription>
        </DialogHeader>

        {!showBankDetails ? (
          <>
            {/* Options de paiement */}
            <div className="grid gap-4 py-4">
              {/* Option Carte Bancaire */}
              <Card 
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  selectedMethod === 'card' 
                    ? 'ring-2 ring-[#d4af37] bg-[#d4af37]/5' 
                    : 'hover:border-[#d4af37]/50'
                }`}
                onClick={() => setSelectedMethod('card')}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${selectedMethod === 'card' ? 'bg-[#d4af37]' : 'bg-gray-100'}`}>
                      <CreditCard className={`h-6 w-6 ${selectedMethod === 'card' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">Carte Bancaire</h4>
                        <Badge className="bg-green-600 text-white font-semibold">Instantané</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Paiement sécurisé par Stripe (CB, Visa, Mastercard)
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <Shield className="h-3 w-3" />
                        <span>Paiement 100% sécurisé</span>
                        <Clock className="h-3 w-3 ml-2" />
                        <span>Activation immédiate</span>
                      </div>
                    </div>
                    {selectedMethod === 'card' && (
                      <CheckCircle className="h-5 w-5 text-[#d4af37]" />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Option Virement Bancaire */}
              <Card 
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  selectedMethod === 'bank_transfer' 
                    ? 'ring-2 ring-[#d4af37] bg-[#d4af37]/5' 
                    : 'hover:border-[#d4af37]/50'
                }`}
                onClick={() => setSelectedMethod('bank_transfer')}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${selectedMethod === 'bank_transfer' ? 'bg-[#d4af37]' : 'bg-gray-100'}`}>
                      <Building2 className={`h-6 w-6 ${selectedMethod === 'bank_transfer' ? 'text-white' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">Virement Bancaire</h4>
                        <Badge className="bg-blue-600 text-white font-semibold">2-3 jours</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Virement SEPA depuis votre banque
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <Info className="h-3 w-3" />
                        <span>Activation après réception du virement</span>
                      </div>
                    </div>
                    {selectedMethod === 'bank_transfer' && (
                      <CheckCircle className="h-5 w-5 text-[#d4af37]" />
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Montant total */}
            <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Montant total</p>
                <p className="text-2xl font-bold text-gray-900">{amount?.toFixed(2)} €</p>
              </div>
              {selectedMethod && (
                <Badge className="bg-[#b8960c] text-white font-semibold">
                  {selectedMethod === 'card' ? 'Carte bancaire' : 'Virement'}
                </Badge>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={handleClose}>
                Annuler
              </Button>
              <Button 
                onClick={handleConfirm}
                disabled={!selectedMethod || loading}
                className="bg-[#d4af37] hover:bg-[#c4a030] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  <>
                    Continuer
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          /* Affichage des coordonnées bancaires */
          <div className="py-4">
            <Alert className="mb-4 bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Votre demande a été enregistrée. Effectuez le virement avec les coordonnées ci-dessous.
              </AlertDescription>
            </Alert>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-gray-900 mb-3">Coordonnées bancaires</h4>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center p-2 bg-white rounded border">
                  <div>
                    <p className="text-xs text-gray-500">Bénéficiaire</p>
                    <p className="font-medium">{BANK_DETAILS.beneficiaire}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard(BANK_DETAILS.beneficiaire, 'Bénéficiaire')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex justify-between items-center p-2 bg-white rounded border">
                  <div>
                    <p className="text-xs text-gray-500">IBAN</p>
                    <p className="font-mono font-medium text-sm">{BANK_DETAILS.iban}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard(BANK_DETAILS.iban.replace(/\s/g, ''), 'IBAN')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex justify-between items-center p-2 bg-white rounded border">
                  <div>
                    <p className="text-xs text-gray-500">BIC/SWIFT</p>
                    <p className="font-mono font-medium">{BANK_DETAILS.bic}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard(BANK_DETAILS.bic, 'BIC')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex justify-between items-center p-2 bg-white rounded border">
                  <div>
                    <p className="text-xs text-gray-500">Banque</p>
                    <p className="font-medium">{BANK_DETAILS.banque}</p>
                  </div>
                </div>

                <div className="flex justify-between items-center p-2 bg-[#d4af37]/10 rounded border border-[#d4af37]/30">
                  <div>
                    <p className="text-xs text-[#d4af37]">Référence à indiquer (OBLIGATOIRE)</p>
                    <p className="font-mono font-bold text-[#d4af37]">{virementReference}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard(virementReference, 'Référence')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex justify-between items-center p-2 bg-white rounded border">
                  <div>
                    <p className="text-xs text-gray-500">Montant exact</p>
                    <p className="font-bold text-lg">{amount?.toFixed(2)} €</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard(amount?.toFixed(2), 'Montant')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <Alert className="mt-4">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Important :</strong> Indiquez la référence <strong>{virementReference}</strong> dans le libellé de votre virement. 
                Votre service sera activé sous 2-3 jours ouvrés après réception.
              </AlertDescription>
            </Alert>

            <DialogFooter className="mt-4">
              <Button onClick={handleClose} className="w-full bg-[#d4af37] hover:bg-[#c4a030] text-white">
                J'ai compris, fermer
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
